import { PureComponent } from 'react';
import LoadingProgress from '@/components/common/LoadingProgress';
import Pannellum from './Pannellum';
import { dLog } from '@/_helpers/renderDebug';

export default class Panorama extends PureComponent<any, any> {
  pannellums: any[] = [];

  scenes: any = {};

  constructor(props: any) {
    super(props);
    this.state = {
      loaded: false,
      current: 0,
    };
  }

  async componentDidUpdate(props: any) {
    if (props.activePano !== this.props.activePano) {
      this.props.panoramas.forEach((p: any) => {
        if (p.id === this.props.activePano) this.loadCurrentScenes(p.name, this.state.current, true);
      });
    }
    if (props.panoPose !== this.props.panoPose) {
      const { hfov, pitch, yaw } = props.panoPose;
      const { h, p, y } = this.props.panoPose;
      if (hfov !== h || pitch !== p || yaw !== y) {
        this.pannellums[this.state.current].setPose(this.props.panoPose);
      }
    }
  }

  async componentDidMount() {
    let firstScene: any;
    this.props.panoramas.forEach((p: any) => {
      this.scenes[p.name] = p;
      if (p.default) firstScene = p;
    });
    // override default if activePano exists
    this.props.panoramas.forEach((p: any) => {
      if (this.props.activePano && this.props.activePano === p.id) firstScene = p;
    });

    const options = {
      loadCurrentScenes: this.loadCurrentScenes,
      updatePanoRotation: this.props.updatePanoRotation,
      openHotspot: this.props.openHotspot,
      onLoaded: this.onPanoLoaded,
      showLoading: this.showLoading,
      loadSelf: this.loadSelf,
      transition: this.props.transition,
    };

    this.pannellums = [
      new Pannellum({
        id: 1,
        autoLoad: false,
        ...options,
      }),
    ];

    if (!options.transition) {
      // eslint-disable-next-line array-callback-return
      Object.keys(this.scenes).map((scene: string) => {
        this.pannellums[0].addScene(this.scenes[scene]);
      });
    } else {
      this.pannellums.push(
        new Pannellum({
          id: 2,
          ...options,
        }),
      );
      this.pannellums.push(
        new Pannellum({
          id: 3,
          ...options,
        }),
      );
      this.pannellums.push(
        new Pannellum({
          id: 4,
          ...options,
        }),
      );
    }

    if (firstScene) {
      this.loadCurrentScenes(firstScene.name, 0, true);

      const { pitch, yaw, hfov } = this.props.panoPose;
      if (pitch || yaw || hfov) {
        this.pannellums[0].setPose(this.props.panoPose);
      }
    }
  }

  onPanoLoaded = () => {
    this.setState({ loaded: true });
  };

  showLoading = (checkGuest?: boolean) => {
    if (checkGuest) {
      if (this.props.isChatGuest) {
        this.setState({ loaded: false });
      }
    } else {
      this.setState({ loaded: false });
    }
  };

  loadSelf = (scene: string) => {
    this.pannellums[0].loadScene({
      scene,
      pitch: this.scenes[scene].startPitch,
      yaw: this.scenes[scene].startYaw,
      hfov: this.scenes[scene].hfov,
    });
  };

  getPanoHotspots = (scene: string) => {
    if (!this.scenes[scene] || !this.scenes[scene].hotspots) return [];
    const hotspots: any = [];
    this.scenes[scene].hotspots.forEach((h: any) => {
      if (h.type === 'panorama') {
        hotspots.push({
          id: `h-${h.id}`,
          scene: h.scene,
          pitch: h.pitch,
          yaw: h.yaw,
        });
      }
    });
    return hotspots;
  };

  loadCurrentScenes = (playerScene: string, x: number, loadCurrent = false) => {
    this.showLoading(true);
    this.pannellums[x].active = true;

    this.props.onSceneChange(this.scenes[playerScene]);

    const { startPitch: pitch, startYaw: yaw, hfov } = this.scenes[playerScene];

    if (loadCurrent) {
      this.pannellums[x].addScene(this.scenes[playerScene]);
      this.pannellums[x].loadScene({
        scene: playerScene,
        pitch,
        yaw,
        hfov,
      });
      this.pannellums[x].setNorthOffset(this.scenes[playerScene].northYaw);
    } else {
      const animationTime = 1000;
      this.pannellums[x].lookAt({
        pitch,
        yaw,
        bounds: hfov,
        time: animationTime,
      });
    }

    this.setState({
      current: x,
    });

    // max 4 hotspots -> forward, backward, left, right
    const panoHotspots = this.getPanoHotspots(playerScene);
    const currentScene = this.scenes[this.pannellums[x].getScene()];
    if (!currentScene) return;
    this.props.markMinimap(currentScene);

    if (!this.props.transition) return;

    // TODO: improve performence by not loading the same panorama twice
    panoHotspots.forEach((h: any, i: number) => {
      const j = (x + i + 1) % 4; // find all the next pannellum instances
      const scene = this.scenes[h.scene];
      if (!scene) return;
      this.pannellums[j].active = false;
      this.pannellums[x].hotspotsMap[h.id] = this.pannellums[j];

      const p = scene.northPitch && currentScene.northPitch ? scene.northPitch - (currentScene.northPitch - h.pitch) : null;
      const y = scene.northYaw && currentScene.northYaw ? scene.northYaw - (currentScene.northYaw - h.yaw) : null;
      this.pannellums[j].addScene(scene);
      this.pannellums[j].loadScene({
        scene: scene.name,
        pitch: p,
        yaw: y,
        hfov: scene.hfov,
      });
      this.pannellums[j].setNorthOffset(scene.northYaw);
    });
  };

  async componentWillUnmount() {
    // eslint-disable-next-line no-restricted-syntax
    for (const pannellum of this.pannellums) {
      pannellum.destroyViewer();
    }
  }

  public render() {
    dLog('Panorama')
    return (
      <div id="panorama-overlay">
        <button className="btn close-btn" onClick={() => this.props.handleTogglePano()}>
          <i className="fas fa-times" />
        </button>
        <div style={{ height: '100%', width: '100%' }}>
          <div id="panorama-player-1" className="panorama show" />
          <div id="panorama-player-2" className="panorama" />
          <div id="panorama-player-3" className="panorama" />
          <div id="panorama-player-4" className="panorama" />
        </div>
        {!this.state.loaded && <LoadingProgress />}
      </div>
    );
  }
}
